import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSelectedAccount } from '@ibk/shared-auth';
import { selectHasAccessData, selectLastCheckedDocument } from '@ibk/shared-config';
import { useTranslation } from '@ibk/shared-translation';
import { DocumentType } from '@ibk/shared-types';
import { Button, Input, useCnpjAlphanumeric, useMaskCpfCnpj } from '@ibk/shared-ui';
import { isValidCpf, maskCpf } from '@ibk/shared-utils';
import { useCreateBelvoTokenMutation } from '../../../../../services/openFinanceApi';
import styles from '../ConsentModal.module.css';
export const ModalForm = ({ handleCloseConsentModal, setToken, }) => {
    const { t } = useTranslation();
    const hasAccessData = useSelector(selectHasAccessData);
    const lastCheckedDocument = useSelector(selectLastCheckedDocument);
    const { selectedAccount } = useSelectedAccount();
    const [isValidCnpjValue, setIsValidCnpjValue] = useState(false);
    const [createBelvoToken, { isLoading: isCreatingToken, error: tokenError }] = useCreateBelvoTokenMutation();
    const { isValidCnpj, unmaskCnpj } = useCnpjAlphanumeric();
    const { maskCpfCnpj } = useMaskCpfCnpj();
    const transformFormDataToBelvoRequest = (data) => {
        const identification = [];
        if (isValidCnpj(data.cpfCnpj)) {
            identification.push({
                type: DocumentType.CNPJ,
                number: unmaskCnpj(data.cpfCnpj),
                name: data.razaoSocial,
            });
            identification.push({
                type: DocumentType.CPF,
                number: data.cpfResponsavel,
                name: data.nomeTitular,
            });
        }
        else {
            identification.push({
                type: DocumentType.CPF,
                number: data.cpfCnpj.replace(/\D/g, ''),
                name: data.nomeTitular,
            });
        }
        return { identification };
    };
    let defaultValues = {
        cpfCnpj: maskCpfCnpj(selectedAccount?.documentNumber || ''),
        razaoSocial: selectedAccount?.companyName || '',
        cpfResponsavel: maskCpf(lastCheckedDocument || ''),
        nomeTitular: hasAccessData?.full_name || '',
    };
    const { register, handleSubmit, reset, control, watch, formState: { errors, isSubmitting }, } = useForm({
        defaultValues: { ...defaultValues },
        mode: 'onChange',
    });
    useEffect(() => {
        if (selectedAccount?.documentNumber) {
            defaultValues = {
                cpfCnpj: maskCpfCnpj(selectedAccount.documentNumber),
                razaoSocial: selectedAccount.companyName,
                cpfResponsavel: maskCpf(lastCheckedDocument || ''),
                nomeTitular: hasAccessData?.full_name || '',
            };
            reset(defaultValues);
        }
    }, [
        selectedAccount?.companyName,
        selectedAccount?.documentNumber,
        lastCheckedDocument,
        hasAccessData?.full_name,
        reset,
    ]);
    useEffect(() => {
        const documentNumber = watch('cpfCnpj').replace(/[^a-zA-Z0-9]/g, '');
        setIsValidCnpjValue(documentNumber.length >= 12);
    }, [watch('cpfCnpj')]);
    const handleFormSubmit = async (data) => {
        try {
            const belvoRequest = transformFormDataToBelvoRequest(data);
            const { result } = await createBelvoToken(belvoRequest).unwrap();
            setToken(result);
        }
        catch (error) {
            console.error('Error creating Belvo token:', error);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.description, "data-testid": 'textViewConsentModalFormDescription', children: t('contabilidade-digital.consent-modal.form-description') }), _jsx("form", { onSubmit: handleSubmit(handleFormSubmit), "data-testid": 'formConsentModalIdentification', children: _jsxs("div", { className: styles.formContainer, "data-testid": 'containerConsentModalFormFields', children: [_jsx(Controller, { name: 'cpfCnpj', control: control, rules: {
                                required: t('contabilidade-digital.consent-modal.cpf-cnpj-required'),
                                validate: {
                                    isValidCpf: value => {
                                        if (isValidCpf(value) || isValidCnpj(value)) {
                                            return true;
                                        }
                                        return t('contabilidade-digital.consent-modal.cpf-cnpj-invalid');
                                    },
                                },
                            }, render: ({ field }) => (_jsx(Input, { id: 'cpfCnpj', label: t('contabilidade-digital.consent-modal.cpf-cnpj-label'), placeholder: t('contabilidade-digital.consent-modal.cpf-cnpj-placeholder'), value: field.value, onChange: e => {
                                    field.onChange(maskCpfCnpj(e.target.value));
                                }, onBlur: field.onBlur, dataTestId: 'inputContabilidadeDashboardCpfCnpj', size: 'large', error: errors.cpfCnpj?.message, required: true })) }), isValidCnpjValue && (_jsxs(_Fragment, { children: [_jsx("div", { children: _jsx(Input, { id: 'razaoSocial', label: t('contabilidade-digital.consent-modal.razao-social-label'), placeholder: t('contabilidade-digital.consent-modal.razao-social-placeholder'), ...register('razaoSocial', {
                                            required: t('contabilidade-digital.consent-modal.razao-social-required'),
                                        }), dataTestId: 'inputContabilidadeDashboardRazaoSocial', size: 'large', error: errors.razaoSocial?.message, required: true }) }), _jsx("div", { children: _jsx(Controller, { name: 'cpfResponsavel', control: control, rules: {
                                            required: t('contabilidade-digital.consent-modal.cpf-responsavel-required'),
                                            validate: {
                                                isValidCpf: value => {
                                                    if (isValidCpf(value)) {
                                                        return true;
                                                    }
                                                    return t('contabilidade-digital.consent-modal.cpf-invalid');
                                                },
                                            },
                                        }, render: ({ field }) => (_jsx(Input, { id: 'cpfResponsavel', label: t('contabilidade-digital.consent-modal.cpf-responsavel-label'), placeholder: t('contabilidade-digital.consent-modal.cpf-responsavel-placeholder'), value: field.value, onChange: e => {
                                                field.onChange(maskCpf(e.target.value));
                                            }, onBlur: field.onBlur, dataTestId: 'inputContabilidadeDashboardCpfResponsavel', size: 'large', error: errors.cpfResponsavel?.message, required: true })) }) })] })), _jsx("div", { children: _jsx(Input, { id: 'nomeTitular', label: t('contabilidade-digital.consent-modal.nome-titular-label'), placeholder: t('contabilidade-digital.consent-modal.nome-titular-placeholder'), ...register('nomeTitular', {
                                    required: t('contabilidade-digital.consent-modal.nome-titular-required'),
                                }), dataTestId: 'inputContabilidadeDashboardNomeTitular', size: 'large', error: errors.nomeTitular?.message, required: true }) }), tokenError && (_jsx("div", { className: styles.errorMessage, "data-testid": 'containerConsentModalErrorMessage', children: _jsx("p", { "data-testid": 'textViewConsentModalErrorText', children: t('contabilidade-digital.consent-modal.connection-error') }) })), _jsxs("div", { className: styles.buttonContainer, "data-testid": 'containerConsentModalFormButtons', children: [_jsx(Button, { type: 'button', variant: 'secondary', size: 'medium', onClick: handleCloseConsentModal, dataTestId: 'buttonContabilidadeDashboardModalCancel', className: styles.button, disabled: isSubmitting || isCreatingToken, children: t('contabilidade-digital.consent-modal.button-back') }), _jsx(Button, { type: 'submit', variant: 'primary', size: 'medium', disabled: isSubmitting || isCreatingToken, className: styles.button, dataTestId: 'buttonContabilidadeDashboardModalConnect', children: isCreatingToken
                                        ? t('contabilidade-digital.consent-modal.button-connecting')
                                        : t('contabilidade-digital.consent-modal.button-advance') })] })] }) })] }));
};
